import { useState } from "react";

import { makeGetApiCall, makePostApiCall } from "../../shared/makeApiCall";
import ModelErrorComponent from "../../components/modals/ModelErrorComponent";
import ProgressModal from "../../components/modals/ProgressModal";
import { SnackbarData, SnackbarType } from "../../components/Snackbar";
import {
  formatSunShippingLabelDateCode,
  generateUniqueNumber,
  isSunDistribution,
} from "../../shared/utility";
import DeleteConfirmationForm from "../../components/forms/DeleteConfirmationForm";
import SunShippingLabelReviewModal from "../../components/forms/shipping/SunShippingLabelReviewModal";
import SunBolReviewModal from "../../components/forms/shipping/SunBolReviewModal";

export function ShippingDetailsViewModelV2(shippingId, logout, addSnackbar) {
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [emptyModal, setEmptyModelOpen] = useState({
    open: false,
    childComponent: null,
  });
  const [shipping, setShipping] = useState(null);
  const [slideOver, setSlideOver] = useState({
    childComponent: null,
    open: false,
    customClass: null,
  });
  const [rowDetails, setRowDetails] = useState({}); // { [rowId]: detailData }
  const [loadingRows, setLoadingRows] = useState({}); // { [rowId]: boolean }
  const [sunCustomers, setSunCustomers] = useState([]);
  const [loading, setLoading] = useState(true);

  const runInitialLoad = async function () {
    setLoading(true);
    try {
      await getEmployeesForDropdown([1, 2], [1, 5]);
      await getShippingDetails();
      await getSunCustomersForDropdown();
    } finally {
      setLoading(false);
    }
  };

  const getShippingDetails = async function () {
    try {
      const [callResult, orderDetails] = await Promise.all([
        makePostApiCall(`fetchShipping/${shippingId}`),
        onFetchOrderItems(),
      ]);
      if (callResult.success && orderDetails.success) {
        const order = orderDetails.data;

        setShipping({
          ...callResult.data,
          order_id: order.id,
          customer: order.customer,
          po_number: callResult.data.po_number ?? order.po_number ?? "",
          order: callResult.data.order ?? {
            value: order.id,
            label: order.po_number || `Order #${order.id}`,
          },
          orderItems: [...order.products],
          shipped_subtotal_dollar_amount: order.shipped_subtotal_dollar_amount,
          shipped_total_dollar_amount: order.shipped_total_dollar_amount,
          total_shipped_cartons: order.total_shipped_cartons,
          expiredOrderItems: order.expired_order_items,
          hasExpiredOrderItems: order.has_order_expired_items,
        });
      } else {
        if (callResult.errorStatus === 401) {
          logout();
        }
        showErrorModal(
          callResult.data?.message ?? "We couldn't load shipping details",
        );
      }
    } catch (err) {
      showErrorModal("We couldn't load shipping details");
    }
  };

  const onFetchOrderItems = async function () {
    let callResult = await makePostApiCall(
      `fetchOrderByShippingId/${shippingId}`,
    );
    if (callResult.success) {
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
    return callResult;
  };

  const showErrorModal = (title, subtitle) => {
    setEmptyModelOpen({
      childComponent: (
        <ModelErrorComponent
          setOpen={setEmptyModelOpen}
          subtitle={subtitle}
          title={title}
        />
      ),
      open: true,
    });
  };

  const showProgressModal = (message = "Processing...") => {
    setEmptyModelOpen({
      childComponent: <ProgressModal message={message} />,
      open: true,
    });
  };

  const hideProgressModal = () => {
    setEmptyModelOpen({
      childComponent: null,
      open: false,
    });
  };

  const updateShippingApiCall = async function (payload, isSubAction = false) {
    showProgressModal("Updating shipping...");
    let callResult = await makePostApiCall(`updateShipping/${shippingId}`, {
      ...payload,
      is_prepaid: payload.is_prepaid ? "1" : "0",
    });
    if (callResult.success) {
      if (callResult.data?.shipping) {
        setShipping((prev) => ({
          ...prev,
          ...callResult.data.shipping,
          // Keep nested/client-only relations that update response may omit
          order: prev?.order,
          customer: prev?.customer,
          orderItems: prev?.orderItems,
          expiredOrderItems: prev?.expiredOrderItems,
          hasExpiredOrderItems: prev?.hasExpiredOrderItems,
          sun_customer: prev?.sun_customer,
          created_by: prev?.created_by,
        }));
      }
      if (!isSubAction) {
        addSnackbar(
          new SnackbarData(
            generateUniqueNumber(),
            callResult.data.message,
            SnackbarType.Success,
          ),
        );
      }
      hideProgressModal();
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
    return callResult;
  };

  const onFetchPackagedItemByBarcode = async function (scannedBarcode) {
    let callResult = await makePostApiCall(
      `fetchPackagedItemByBarcode/${scannedBarcode}`,
    );
    if (callResult.success) {
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
    return callResult;
  };

  const addPackagedItemToOrderItem = async function (
    shippingId,
    orderItem,
    packagedItem,
  ) {
    const payload = {
      shippingId: shippingId,
      orderItem: orderItem,
      packagedItem: packagedItem,
    };
    let callResult = await makePostApiCall(
      "addPackagedItemToOrderItem",
      payload,
    );
    if (callResult.success) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          "Package added to shipping",
          SnackbarType.Success,
        ),
      );
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
    return callResult;
  };

  const deleteShippingItemApiCall = async function (id, serial_number) {
    let callResult = await makePostApiCall(`deleteShippingItem/${id}`);
    if (callResult.success) {
      // Remove from expired items if it exists
      if (serial_number && shipping?.expiredOrderItems) {
        const filteredExpiredItems = shipping.expiredOrderItems.filter(
          (item) => item.serial_number !== serial_number,
        );
        setShipping((prev) => ({
          ...prev,
          expiredOrderItems: filteredExpiredItems,
          hasExpiredOrderItems: filteredExpiredItems.length > 0,
        }));
      }

      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          callResult.data.message,
          SnackbarType.Success,
        ),
      );
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
    return callResult;
  };

  const addSkidLabelToOrderItem = async function (
    shippingId,
    orderItemId,
    skidLabelNumber,
  ) {
    const payload = {
      shippingId: shippingId,
      orderItemId: orderItemId,
      packaged_items: skidLabelNumber,
    };
    let callResult = await makePostApiCall(
      "addSkidLabelNumberToOrderItem",
      payload,
    );
    if (callResult.success) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          "Packages added to shipping",
          SnackbarType.Success,
        ),
      );
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
    return callResult;
  };

  const getEmployeesForDropdown = async function (type, department) {
    const payload = { department: department, type: type };
    let callResult = await makePostApiCall(`employeesForDropdown`, payload);

    if (callResult.success) {
      setEmployeeOptions(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
    return callResult;
  };

  const deleteShipping = async function () {
    let callResult = await makePostApiCall(`shippings/delete/${shippingId}`);

    if (callResult.success) {
      window.location = "/shippings";
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.error);
    }
  };

  const printShipping = async function (formValues) {
    // Save form data first
    const updateResult = await updateShippingApiCall(formValues, true);
    if (!updateResult.success) {
      return;
    }

    showProgressModal("Printing shipping slip...");

    try {
      let callResult = await makePostApiCall(`printShipping/${shippingId}`);

      hideProgressModal();
      if (callResult.success) {
        window.open(callResult.data.location, "_blank");
      } else {
        if (callResult.errorStatus === 401) {
          logout();
        }
        showErrorModal(callResult.data.message);
      }
    } catch (error) {
      hideProgressModal();
      showErrorModal(
        "Error",
        "An error occurred while printing the shipping label.",
      );
    }
  };

  const quickAddPackagedItemToOrderItem = async function (newValues) {
    let callResult = await makePostApiCall(
      "quickAddPackagedItemToOrderItemV2",
      {
        ...newValues,
        quantity: parseInt(newValues.quantity),
        shipping_id: shippingId,
      },
    );
    if (callResult.success) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          "Package(s) added to shipping",
          SnackbarType.Success,
        ),
      );
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
    return callResult;
  };

  const deleteQuickScanShippingItemByLot = async function (payload) {
    let callResult = await makePostApiCall(
      `deleteQuickScanShippingItemByLot`,
      payload,
    );
    if (callResult.success) {
      // Remove from expired items if serial_numbers provided
      if (payload.serial_numbers && shipping?.expiredOrderItems) {
        // remove for accordion display
        const filteredExpiredItems = shipping.expiredOrderItems.filter(
          (item) => !payload.serial_numbers.includes(item.serial_number),
        );
        setShipping((prev) => ({
          ...prev,
          expiredOrderItems: filteredExpiredItems,
          hasExpiredOrderItems: filteredExpiredItems.length > 0,
        }));

        addSnackbar(
          new SnackbarData(
            generateUniqueNumber(),
            "Package(s) deleted from shipping",
            SnackbarType.Success,
          ),
        );
      }
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("We could not delete the item");
    }
    return callResult;
  };

  const exportOrderForNetSuite = async function () {
    let callResult = await makePostApiCall(`exportOrderForNetSuite`, {
      shipments: [shippingId],
    });
    if (callResult.success) {
      window.open(callResult.data.location, "_blank").focus();
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
  };

  const checkForExpiredItems = async function () {
    let callResult = await makeGetApiCall(
      `checkForExpiredItems/${shipping.order_id}`,
    );
    if (callResult.success) {
      return callResult.data;
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
    return callResult;
  };

  const getOrderItemsOnly = async function () {
    let callResult = await makeGetApiCall(
      `getOrderItemsOnlyV2/${shipping.order_id}`,
    );
    if (callResult.success) {
      setRowDetails({});
      setLoadingRows({});
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
    return callResult;
  };

  const fetchRowDetails = async (rowData) => {
    setLoadingRows((prev) => ({ ...prev, [rowData.id]: true }));

    let callResult = await makeGetApiCall(
      `getOrderItemShipments/${rowData.id}`,
    );
    if (callResult.success) {
      setRowDetails({
        ...rowDetails,
        [rowData.id]: callResult.data.shippingItems,
      });
      setLoadingRows({ ...loadingRows, [rowData.id]: false });
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const getSunCustomersForDropdown = async function () {
    let callResult = await makeGetApiCall(`getSunCustomersForDropdown`);
    if (callResult.success) {
      setSunCustomers(callResult.data);
    } else {
      showErrorModal(callResult.data.message);
    }
  };

  const sendShippingToQuickbooks = async function (formValues) {
    // Save form data first and then check if the shipment is marked as shipped
    const updateResult = await updateShippingApiCall(formValues, true);
    if (!updateResult.success) {
      return;
    }

    const getStatusValue = (status) => {
      if (status && typeof status === "object" && "value" in status) {
        return status.value;
      }
      return status;
    };

    // The backend expects status `5` to represent "shipped".
    const statusValue = getStatusValue(formValues?.status);
    const statusValueNum =
      typeof statusValue === "string" ? parseInt(statusValue, 10) : statusValue;

    const poNumber =
      shipping?.order.label ??
      "N/A";

    const sendToQuickbooksSubAction = async () => {
      showProgressModal("Sending shipping to Quickbooks...");
      let callResult = await makePostApiCall(
        `sunDistribution/sendShippingToQuickbooks`,
        {
          shipment_id: parseInt(shippingId),
        },
      );
      hideProgressModal();
      if (callResult.success) {
        addSnackbar(
          new SnackbarData(
            generateUniqueNumber(),
            "Shipping sent to Quickbooks",
            SnackbarType.Success,
          ),
        );
        setShipping((prev) => ({
          ...prev,
          sun_quickbooks_po_id: callResult.data.purchase_order.qb_po_id,
          sun_quickbooks_invoice_id:
            callResult.data.invoice.sun_quickbooks_invoice_id,
          invoice_number: callResult.data.invoice.invoice_number,
        }));
      } else {
        showErrorModal(callResult.data.message);
      }
    };

    if (statusValueNum !== 5) {
      setEmptyModelOpen({
        childComponent: (
          <DeleteConfirmationForm
            title="Send to QuickBooks"
            buttonMessage="Send anyway"
            confirmAction={() => {
              setEmptyModelOpen({ open: false, childComponent: null });
              sendToQuickbooksSubAction();
            }}
            message={`This shipment is not marked as shipped yet. Are you sure you want to send it to QuickBooks anyway? (PO Number: ${poNumber})`}
            setOpen={setEmptyModelOpen}
          />
        ),
        open: true,
      });
      return;
    }

    await sendToQuickbooksSubAction();
  };

  const createSolmazInvoice = async function (formValues) {
    const customer = formValues?.customer;
    const customerName =
      (typeof customer === "string" && customer) ||
      customer?.label ||
      customer?.name ||
      "this customer";

    const createInvoiceSubAction = async () => {
      showProgressModal("Creating QuickBooks invoice...");
      let callResult = await makePostApiCall(
        `solmaz/quickbooks/createInvoice`,
        {
          shipment_id: parseInt(shippingId),
        },
      );
      hideProgressModal();
      if (callResult.success) {
        addSnackbar(
          new SnackbarData(
            generateUniqueNumber(),
            callResult.data?.message ?? "Invoice created",
            SnackbarType.Success,
          ),
        );
        setShipping((prev) => ({
          ...prev,
          solmaz_quickbooks_invoice_id:
            callResult.data.solmaz_quickbooks_invoice_id,
          invoice_number:
            callResult.data.invoice_number ?? prev?.invoice_number,
        }));
      } else {
        showErrorModal(
          callResult.data?.error ?? callResult.data?.message,
        );
      }
    };

    setEmptyModelOpen({
      childComponent: (
        <DeleteConfirmationForm
          title="Create QuickBooks Invoice"
          buttonMessage="Create"
          confirmButtonClassName="bg-main-purple hover:bg-indigo-600"
          confirmAction={() => {
            setEmptyModelOpen({ open: false, childComponent: null });
            createInvoiceSubAction();
          }}
          message={`Do you want to create a QuickBooks invoice for customer "${customerName}"?`}
          setOpen={setEmptyModelOpen}
        />
      ),
      open: true,
    });
  };

  const buildSunShippingLabelData = function (info, orderItem, packagingItem) {
    return {
      label_type: info.label_type,
      shipping_id: parseInt(shippingId),
      order_item_id: parseInt(orderItem.id),
      product_lot: packagingItem.product_lot,
      ship_from: { ...info.ship_from },
      ship_to: { ...info.ship_to },
      po_number: info.po_number ?? "",
      location_id: info.location_id ?? "",
      customer_item_number: info.customer_item_number ?? "",
      date_code: formatSunShippingLabelDateCode(
        packagingItem.best_before_date,
      ),
      carrier: info.carrier ?? "",
      bill_of_lading: info.bill_of_lading ?? "",
      pro_number: info.pro_number ?? "",
    };
  };

  const fetchSunShippingLabelInfoV2 = async function (orderItemId) {
    return makePostApiCall(`sunDistribution/getSunShippingLabelInfoV2`, {
      shipping_id: parseInt(shippingId),
      order_item_id: parseInt(orderItemId),
    });
  };

  const printSunShippingLabelV2 = async function (labelData) {
    return makePostApiCall(`sunDistribution/printSunShippingLabelV2`, {
      label_data: labelData,
    });
  };

  const openSunShippingLabelReview = async function (orderItem, packagingItem) {
    showProgressModal("Loading shipping label...");
    const infoResult = await fetchSunShippingLabelInfoV2(orderItem.id);
    hideProgressModal();

    if (!infoResult.success) {
      if (infoResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(
        infoResult.data?.message ?? "Could not load shipping label info",
      );
      return;
    }

    const initialLabelData = buildSunShippingLabelData(
      infoResult.data,
      orderItem,
      packagingItem,
    );

    const resetLabelData = async () => {
      const resetResult = await fetchSunShippingLabelInfoV2(orderItem.id);
      if (!resetResult.success) {
        showErrorModal(
          resetResult.data?.message ?? "Could not reset shipping label info",
        );
        return null;
      }
      return buildSunShippingLabelData(
        resetResult.data,
        orderItem,
        packagingItem,
      );
    };

    setSlideOver({
      open: true,
      customClass: "max-w-5xl",
      childComponent: (
        <SunShippingLabelReviewModal
          setSlideOver={setSlideOver}
          labelInfo={infoResult.data}
          initialLabelData={initialLabelData}
          orderItem={orderItem}
          packagingItem={packagingItem}
          isPrinting={false}
          onReset={resetLabelData}
          onPrint={async (labelData) => {
            showProgressModal("Printing shipping label...");
            const printResult = await printSunShippingLabelV2(labelData);
            hideProgressModal();

            if (printResult.success) {
              window.open(printResult.data.location, "_blank");
              setSlideOver({
                childComponent: null,
                open: false,
                customClass: null,
              });
            } else {
              if (printResult.errorStatus === 401) {
                logout();
              }
              showErrorModal(
                printResult.data?.message ?? "Could not print shipping label",
              );
            }
          }}
        />
      ),
    });
  };

  const printShippingLabel = async function (orderItemId) {
    showProgressModal("Printing shipping label...");
    let callResult = await makePostApiCall(
      `sunDistribution/printSunShippingLabel`,
      {
        shipping_id: parseInt(shippingId),
        order_item_id: parseInt(orderItemId),
      },
    );
    hideProgressModal();
    if (callResult.success) {
      window.open(callResult.data.location, "_blank");
    } else {
      showErrorModal(callResult.data.message);
    }
  };

  const calculateFreightWeight = async function (palletCount, formValues) {
    // Save form data first
    const updateResult = await updateShippingApiCall(formValues, true);
    if (!updateResult.success) {
      return updateResult;
    }

    showProgressModal("Calculating freight weight...");
    let callResult = await makePostApiCall(
      `sunDistribution/calculateWalmartFreight`,
      {
        shipment_id: parseInt(shippingId),
        pallet_count: parseInt(palletCount),
      },
    );
    hideProgressModal();
    if (callResult.success) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          "Freight weight calculated",
          SnackbarType.Success,
        ),
      );
    } else {
      showErrorModal(callResult.data.message);
    }
    return callResult;
  };

  const fetchSunBolInfoV2 = async function () {
    return makePostApiCall(`sunDistribution/getSunBolInfoV2`, {
      shipping_id: parseInt(shippingId),
    });
  };

  const printSunBolV2 = async function (bolData) {
    return makePostApiCall(`sunDistribution/printSunBolV2`, {
      bol_data: bolData,
    });
  };

  const generateBolDocument = async function (formValues) {
    const updateResult = await updateShippingApiCall(formValues, true);
    if (!updateResult.success) {
      return;
    }

    showProgressModal("Loading Bill of Lading...");
    const infoResult = await fetchSunBolInfoV2();
    hideProgressModal();

    if (!infoResult.success) {
      if (infoResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(
        infoResult.data?.message ?? "Could not load Bill of Lading info",
      );
      return;
    }

    const initialBolData = {
      ...infoResult.data,
      po_number:
        infoResult.data.po_number || formValues.sun_customer_po_number || "",
    };

    const resetBolData = async () => {
      const resetResult = await fetchSunBolInfoV2();
      if (!resetResult.success) {
        showErrorModal(
          resetResult.data?.message ?? "Could not reset Bill of Lading info",
        );
        return null;
      }
      return resetResult.data;
    };

    setSlideOver({
      open: true,
      customClass: "max-w-7xl",
      childComponent: (
        <SunBolReviewModal
          setSlideOver={setSlideOver}
          initialBolData={initialBolData}
          isPrinting={false}
          onReset={resetBolData}
          onPrint={async (bolData) => {
            showProgressModal("Generating BOL document...");
            const printResult = await printSunBolV2(bolData);
            hideProgressModal();

            if (printResult.success) {
              window.open(printResult.data.location, "_blank");
              setSlideOver({
                childComponent: null,
                open: false,
                customClass: null,
              });
            } else {
              if (printResult.errorStatus === 401) {
                logout();
              }
              showErrorModal(
                printResult.data?.message ?? "Could not generate Bill of Lading",
              );
            }
          }}
        />
      ),
    });
  };

  const generateBolNumber = async function () {
    showProgressModal("Generating BOL number...");
    let callResult = await makeGetApiCall(
      `sunDistribution/generateNextWalmartBolNumber`,
    );
    hideProgressModal();
    if (callResult.success) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          "BOL number generated",
          SnackbarType.Success,
        ),
      );
      setShipping((prev) => ({
        ...prev,
        sun_bol_number: callResult.data.next_bol_number,
      }));
    } else {
      showErrorModal(callResult.data.message);
    }
    return callResult;
  };

  const updateOrderItemUnitPrice = async function (orderItemId, unitPrice) {
    let callResult = await makePostApiCall(`updateOrderItemUnitPrice`, {
      order_item_id: parseInt(orderItemId),
      unit_price: parseFloat(unitPrice),
      is_sun_distribution: isSunDistribution(),
    });
    if (!callResult.success) {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
    return callResult;
  };

  return {
    onFetchOrderItems,
    setEmptyModelOpen,
    setSlideOver,
    slideOver,
    emptyModal,
    showErrorModal,
    updateShippingApiCall,
    shipping,
    onFetchPackagedItemByBarcode,
    addPackagedItemToOrderItem,
    getShippingDetails,
    runInitialLoad,
    loading,
    deleteShippingItemApiCall,
    addSkidLabelToOrderItem,
    employeeOptions,
    getEmployeesForDropdown,
    deleteShipping,
    printShipping,
    quickAddPackagedItemToOrderItem,
    deleteQuickScanShippingItemByLot,
    exportOrderForNetSuite,
    getOrderItemsOnly,
    rowDetails,
    setRowDetails,
    fetchRowDetails,
    loadingRows,
    sunCustomers,
    getSunCustomersForDropdown,
    sendShippingToQuickbooks,
    createSolmazInvoice,
    openSunShippingLabelReview,
    printShippingLabel,
    calculateFreightWeight,
    generateBolDocument,
    generateBolNumber,
    updateOrderItemUnitPrice,
    checkForExpiredItems,
  };
}
